import { SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useThemeStore, type Theme } from '@/store';
import { settingsService } from '@/services';
import { cn } from '@/utils';

/**
 * Push the choice back to the account, so it reaches the user's other devices.
 *
 * Deliberately fire-and-forget and deliberately silent:
 *  - the local store has ALREADY applied and persisted the change, so the theme
 *    is correct on this device whatever the server says;
 *  - a failed write must not surface an error toast for something the user can
 *    see worked, and a successful one must not toast on every single click
 *    (which is why this does not go through useUpdatePreferences, whose
 *    onSuccess toasts "Preferences updated successfully").
 *
 * ThemeToggle renders only inside Header -> DashboardLayout, which is behind
 * ProtectedRoute, so there is always a session to write to.
 */
const persistThemeToAccount = (theme: Theme) => {
  void settingsService.updatePreferences({ theme }).catch(() => {
    /* local choice already applied; nothing useful to say to the user */
  });
};

interface ThemeToggleProps {
  className?: string;
  showLabel?: boolean;
}

export function ThemeToggle({ className, showLabel = false }: ThemeToggleProps) {
  const { theme, resolvedTheme, setTheme, toggleTheme } = useThemeStore();

  const chooseTheme = (next: Theme) => {
    setTheme(next);
    persistThemeToAccount(next);
  };

  const flipTheme = () => {
    toggleTheme();
    persistThemeToAccount(useThemeStore.getState().theme);
  };

  const themes: { value: Theme; label: string; icon: typeof SunIcon }[] = [
    { value: 'light', label: 'Light', icon: SunIcon },
    { value: 'dark', label: 'Dark', icon: MoonIcon },
    { value: 'system', label: 'System', icon: ComputerDesktopIcon },
  ];

  const CurrentIcon = resolvedTheme === 'dark' ? MoonIcon : SunIcon;

  if (!showLabel) {
    // Simple toggle button
    return (
      <button
        onClick={flipTheme}
        className={cn(
          'p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors',
          className
        )}
        aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {resolvedTheme === 'dark' ? (
          <SunIcon className="h-5 w-5" />
        ) : (
          <MoonIcon className="h-5 w-5" />
        )}
      </button>
    );
  }

  // Dropdown with all options
  return (
    <Menu as="div" className={cn('relative', className)}>
      <Menu.Button className="flex items-center gap-2 p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors">
        <CurrentIcon className="h-5 w-5" />
        <span className="text-sm font-medium">
          {themes.find((t) => t.value === theme)?.label}
        </span>
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-40 origin-top-right rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="p-1">
            {themes.map(({ value, label, icon: Icon }) => (
              <Menu.Item key={value}>
                {({ active }) => (
                  <button
                    onClick={() => chooseTheme(value)}
                    className={cn(
                      'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md transition-colors',
                      active
                        ? 'bg-gray-100 dark:bg-gray-700'
                        : 'text-gray-700 dark:text-gray-300',
                      theme === value && 'text-primary-600 dark:text-primary-400'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
